import type { components } from '../api.generated';
import type { LogEntry, LogLevel, LogLevelValue, Transport } from './logger.config';

import { client } from '..';
import { LOG_LEVEL_MAPPER, LOG_LEVELS } from './logger.config';

type CreateAppLogCommand = components['schemas']['CreateAppLogCommand'];
type AppLogLevel = components['schemas']['AppLogLevel'];
type AppKind = components['schemas']['AppKind'];

/**
 * Configuration options for the API transport.
 * Defines settings for API-based logging.
 */
export type ApiTransportOptions = {
    /** Minimum log level for API logging */
    level?: LogLevel;
    /** Default application kind for logs */
    appKind?: AppKind;
    /** Default application URL for logs */
    appUrl?: string;
    /** Default API URL for logs */
    apiUrl?: string;
};

/**
 * API transport for logging.
 * Sends log entries to the server via API endpoints.
 * Supports both app logs and command logs with proper error handling.
 */
export class ApiTransport implements Transport {
    /** The minimum log level this transport will process */
    public level: LogLevel;

    /**
     * Creates a new API transport instance.
     * @param options Configuration options for the API transport
     */
    constructor(options: ApiTransportOptions = {}) {
        this.level = options.level || 'INFO';
    }

    /**
     * Processes and sends a log entry to the appropriate API endpoint.
     * Falls back to console logging if the API call fails.
     * @param entry The log entry to process and send
     */
    public async log(entry: LogEntry): Promise<void> {
        // Check if the log level is enabled for this transport
        if (entry.level < LOG_LEVELS[this.level]) {
            return; // If the log level is not enabled, do not log
        }

        const { level, messages, version, metadata } = entry;
        const formattedMessages = this.formatMessages(messages);
        const timestamp = entry.timestamp.toISOString();

        // Prepare the log payload according to the API schema
        const payload: CreateAppLogCommand = {
            LogLevel: this.getLevelName(level),
            Date: timestamp,
            Message: formattedMessages.join(' '),
            Exception: this.getExceptionMessage(messages),
            AppVersion: version,
            AppKind: metadata?.appKind ?? null,
            AppUrl: metadata?.appUrl ?? null,
            ApiUrl: metadata?.apiUrl ?? null,
            ApiHttpMethod: metadata?.apiHttpMethod ?? null,
            ApiRequestId: metadata?.apiRequestId ?? null,
            DeviceId: metadata?.deviceId ?? null,
            DeviceName: metadata?.deviceName ?? null,
        };

        try {
            // Send to appropriate endpoint based on log type
            if (this.isCommandLog(entry)) {
                await this.sendCommandLog(payload);
            }
            else {
                await this.sendAppLog(payload);
            }
        }
        catch (error) {
            // Fallback to console if API call fails
            console.error('Failed to send log to API:', error);
            // eslint-disable-next-line no-console
            console.log(`[${timestamp}] ${entry.loggerName} (${version}): ${formattedMessages.join(' ')}`);
        }
    }

    /**
     * Sends a log entry to the app logs endpoint.
     * @param payload The formatted log payload to send
     */
    private async sendAppLog(payload: CreateAppLogCommand): Promise<void> {
        await client.POST('/api/v2/apps/logs/create', {
            body: payload,
        });
    }

    /**
     * Sends a log entry to the command logs endpoint.
     * @param payload The formatted log payload to send
     */
    private async sendCommandLog(payload: CreateAppLogCommand): Promise<void> {
        await client.POST('/api/v2/apps/logs/commands/create', {
            body: payload,
        });
    }

    /**
     * Formats log messages into an array of strings.
     * Handles different types of messages (strings, errors, objects).
     * @param messages The messages to format
     * @returns Array of formatted message strings
     */
    private formatMessages(messages: any[]): string[] {
        return messages.map((msg) => {
            if (typeof msg === 'string') {
                return msg;
            }
            if (msg instanceof Error) {
                return msg.message;
            }
            try {
                return JSON.stringify(msg);
            }
            catch {
                return '[Unserializable Object]';
            }
        });
    }

    /**
     * Converts a numeric log level to its API string representation.
     * @param level The numeric log level value
     * @returns The corresponding API log level string
     */
    private getLevelName(level: LogLevelValue): AppLogLevel {
        const logLevel = Object.entries(LOG_LEVELS).find(([_, value]) => value === level)?.[0] as LogLevel || 'INFO';

        return LOG_LEVEL_MAPPER[logLevel] || 'Info';
    }

    /**
     * Extracts exception information from log messages.
     * @param messages The log messages to check for errors
     * @returns The exception message or null if no error found
     */
    private getExceptionMessage(messages: any[]): string | null {
        const error = messages.find(msg => msg instanceof Error);
        if (error instanceof Error) {
            return error.stack || error.message;
        }
        return null;
    }

    /**
     * Determines if a log entry is a command log.
     * @param entry The log entry to check
     * @returns True if the entry is a command log, false otherwise
     */
    private isCommandLog(entry: LogEntry): boolean {
        // Check if the log entry is a command log based on some criteria
        // This is a placeholder - implement your own logic to determine if it's a command log
        return entry.loggerName.toLowerCase().includes('command');
    }
}
